import { motion } from 'motion/react';
import MarkdownTextTailwind from '@/components/markdown/markdown-text-tailwind';
import { BenchmarkCategory } from '@/api/generated-catch-backend/model';
import useAnalyticsCalls from '@/hooks/use-analytics';

interface HeaderTitleProps {
  name?: string;
  benchmark?: BenchmarkCategory;
  className?: string;
  methodologyUrl?: string;
  lifetimeRisk?: number;
  populationAverage?: number;
  userGender?: 'male' | 'female' | null;
}



export default function HeaderTitle({
  name,
  benchmark,
  className = '',
  methodologyUrl,
  lifetimeRisk,
  populationAverage,
  userGender,
}: HeaderTitleProps) {
  const { callLinkClicked } = useAnalyticsCalls();
  
  const titleContent = name && benchmark ? `Hi ${name}, your overall risk is **${benchmark.toLowerCase()}**` : '';
  const genderText = userGender === 'male' ? 'men' : 'women';
  const subtitleContent = lifetimeRisk && populationAverage && userGender
    ? `Your estimated lifetime risk of developing cancer is **${lifetimeRisk}%**, compared to the U.S. average of **${populationAverage}%** for ${genderText} your age.`
    : '';
  return (
    <motion.div
      className={`flex flex-col items-start text-left w-full h-full hidden md:flex ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="flex-1 flex flex-col justify-between w-full h-full py-4">
        <motion.h1
          className="text-white text-4xl md:text-5xl font-normal leading-tight text-left"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <MarkdownTextTailwind>
            {titleContent}
          </MarkdownTextTailwind>
        </motion.h1>

        {/* Divider */}
        <motion.div
          className="w-full h-px bg-white/20"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />

        {subtitleContent && (
          <motion.div
            className="text-white/80 text-base md:text-lg leading-relaxed text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <MarkdownTextTailwind>
              {subtitleContent}
            </MarkdownTextTailwind>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <a
            href={methodologyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold leading-[1.3] text-[#7cf1c7] text-[16px] underline cursor-pointer hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#7cf1c7] focus:ring-opacity-50 rounded-sm"
            aria-label="Learn how your cancer risk is calculated (opens in new tab)"
            onClick={() => callLinkClicked('Learn how your risk is calculated', methodologyUrl || '')}
          >
            Learn how your risk is calculated
          </a>
        </motion.div>
      </div>
    </motion.div>
  );
}
